/**
 * Angular imports
 */
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { AfterContentInit, Component, ContentChildren, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnChanges, output, QueryList, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


/**
 * Application imports
 */
import { TabComponent } from './tab/tab';

interface ProjectedTab {
  component: TabComponent,
  label: string,
  selected: boolean,
  id: string,
  targetId: string
}

export interface TabSelectEvent {
  index: number;
  label: string;
  tabId: string;
}

export type TabVariant = 'default' | 'baseline' | 'container' | 'scrolling' | 'vertical';

@Component({
  selector: 'gene-tabs',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
  ],
  templateUrl: './tabs.html',
  styleUrl: './tabs.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TabsComponent implements OnChanges, AfterContentInit {
  /** Contains all the <gene-tab> children projected into this component. */
  @ContentChildren(TabComponent)
  tabs!: QueryList<TabComponent>;

  /**
   * The id of the currently active tab.
   * Only needed for programmatic control.
   */
  activeTab = input<string>();

  /**
   * Tab variant to apply different styles.
   */
  variant = input<TabVariant>('default');

  /**
   * Emits when the active tab changes.
   */
  tabSelect = output<TabSelectEvent>();

  /**
   * Internal structure to hold tab data.
   */
  protected projectedTabs: ProjectedTab[] = [];

  private activeTabId: string | undefined;
  private destroyRef = inject(DestroyRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab']
        && !changes['activeTab'].isFirstChange()
        && this.activeTabId !== changes['activeTab'].currentValue) {
      this.setProjectedTabs();
      this.programmaticTabSelect();
    }
  }

  ngAfterContentInit() {
    this.setProjectedTabs();

    this.tabs.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setProjectedTabs();
      });
  }

  private setProjectedTabs(): void {
    this.setActiveTab();

    const isAnyTabSelected = this.tabs.toArray().some(tab => this.getTabId(tab) === this.activeTabId);
    let isTabSelected = (tabComponent: TabComponent, index: number) => {
      if (isAnyTabSelected) {
        return this.getTabId(tabComponent) === this.activeTabId;
      } else {
        return index === 0;
      }
    }

    this.projectedTabs = this.tabs.toArray().map((tabComponent, index) => {
      return {
        component: tabComponent,
        label: tabComponent.label(),
        id: this.getTabId(tabComponent),
        targetId: this.getTabId(tabComponent),
        selected: isTabSelected(tabComponent, index),
      };
    });
  }

  private setActiveTab() {
    this.activeTabId = this.activeTab() || this.getTabId(this.tabs.first);
  }

  private getTabId(tab: TabComponent): string {
    return tab.id() || `${tab.label().replace(/\s+/g, '-').toLowerCase()}-tab`;
  }

  handleCoreTabSelect(index: number, label: string, tabId: string): void {
    const selectedTab = this.projectedTabs[index];
    if (selectedTab && selectedTab.targetId !== this.activeTabId) {
      this.activeTabId = selectedTab.targetId;
      this.tabSelect.emit({ index, label, tabId });
    }
  }

  private programmaticTabSelect(): void {
    const activeTabIndex = this.projectedTabs.findIndex(tab => tab.id === this.activeTab());
    const activeTab = this.projectedTabs[activeTabIndex];
    const activeTabLabel = activeTab.label;
    const activeTabId = activeTab.id;
    this.tabSelect.emit({ index: activeTabIndex, label: activeTabLabel, tabId: activeTabId });
  }
}
